/* eslint-disable */
// @ts-nocheck
import React from 'react';
import debugLib from 'debug';
import PropTypes from 'prop-types';
import * as R from 'ramda';

import classNames from 'classnames';
import ButtonIcon from './ButtonIcon';
import Input from './Input';
import Link from './Link';

import { TIME_TO_DISPLAY_MESSAGE } from '../../support/utils';

const debug = debugLib('Copy');

/**
 * Generic Copy Component
 */
class Copy extends React.PureComponent {
  static propTypes = {
    /**
     * Enable user to change content from the input component
     */
    enableEdit: PropTypes.bool,
    /**
     * Custom function to format the value
     */
    formatFunction: PropTypes.func,
    /**
     * Class name for input component
     */
    inputClassName: PropTypes.string,
    /**
     * Class name for button component
     */
    buttonClassName: PropTypes.string,
    /**
     * Class name for container
     */
    className: PropTypes.string,
    /**
     * Value to show on input box to copy
     */
    value: PropTypes.string,
    /**
     * Enable user use a custom input component
     */
    customInputComponent: PropTypes.element,
    /**
     * id for automation testing
     */
    testid: PropTypes.string,
    /**
     * optional i13nModel - default label of "Copy" applied unless passed in
     */
    i13nModel: PropTypes.object,
    /**
     * Class name for input box
     */
    inputBoxClassName: PropTypes.string,
    /**
     * Show a link to the provided value instead of an input
     */
    valueIsUrl: PropTypes.bool,
    /**
     * Show a static text in the input box
     */
    text: PropTypes.string,
  };

  static defaultProps = {
    enableEdit: true,
    formatFunction: R.identity,
    customInputComponent: null,
    testid: 'copy',
  };

  constructor(props) {
    super(props);
    // don't need to store in state as its not UI change
    this.textArea = null;
    this.supportCopy = false;

    this.state = {
      success: false,
      value: '',
      customValue: '',
    };
  }

  componentWillMount() {
    this.supportCopy = document.queryCommandSupported && document.queryCommandSupported('copy');

    this.setState({
      value: this.props.value,
    });
  }

  componentWillReceiveProps(nextProps) {
    const { value = '' } = nextProps;
    this.setState({ value });
  }

  componentWillUnmount() {
    this.destroyTextAreaShim();
  }

  /**
   * Execute copy action
   * if support copy we will use native function
   */
  executeCopyAction = () => {
    let succeeded = true;
    if (this.supportCopy) {
      try {
        this.textArea.select();
        succeeded = document.execCommand('copy');
      } catch (e) {
        debug(e);
      }
      this.setState({
        success: succeeded,
      });
    } else {
      const { formatFunction } = this.props;
      this.setState({
        customValue: formatFunction && formatFunction.call(this, this.state.value),
      });

      // moving to next thread
      setTimeout(() => {
        // safari doesn't support copy until iOS 10
        this.refs['copy-input'].focus();
        this.refs['copy-input'].setSelectionRange(0, this.state.customValue.length);

        this.setState({
          success: succeeded,
        });
      }, 0);
    }
  };

  /**
   * Create text area shim to copy text
   */
  createTextAreaShim = () => {
    if (!this.supportCopy) {
      return;
    }
    if (!this.textArea) {
      const value = this.props.formatFunction.call(this, this.state.value);
      var textArea = document.createElement('textarea');
      textArea.setAttribute('class', 'copy__textarea-shim');
      textArea.setAttribute('readonly', '');
      textArea.value = value;
      debug('appending text area', textArea);
      document.body.appendChild(textArea);
      this.textArea = textArea;
    }
  };

  /**
   * Remove text area
   */
  destroyTextAreaShim = () => {
    if (this.textArea) {
      debug('destroying text area', this.textArea);
      document.body.removeChild(this.textArea);
      this.textArea = null;
    }
  };

  /**
   * Copy text to clipboard
   */
  copyToClipboard = () => {
    debug('#copyToClipboard');
    this.createTextAreaShim();
    this.executeCopyAction();
    this.destroyTextAreaShim();
    // remove message after 2 seconds
    setTimeout(() => {
      this.setState({
        success: false,
      });
    }, TIME_TO_DISPLAY_MESSAGE);
  };

  /**
   * Update input value if input is editable
   * @param {Object} e payload
   */
  updateInputValue = (e) => {
    const { value, error } = e;
    if (!error) {
      this.setState({
        value,
      });
    }
  };

  _getText = (): string => {
    const { text } = this.props;
    if (text) {
      return text;
    }
    return this.state.customValue || this.state.value;
  };

  render() {
    const state = this.state;
    const props = this.props;
    const successMessage = this.supportCopy ? 'Copied to clipboard' : 'Press command ⌘ + c to copy';
    const i13nModel = R.merge({ label: 'Copy' }, this.props.i13nModel);

    const displayValue = this._getText();

    return (
      <div
        className={classNames('copy h_w-100', props.className, {
          'copy--supported': this.supportCopy,
        })}
        data-testid={props.testid}
      >
        {props.valueIsUrl ? (
          <div className="copy__input">
            <div className={classNames(props.inputClassName, props.inputBoxClassName)}>
              <Link testid="link-url" href={displayValue}>
                {displayValue}
              </Link>
            </div>
            <span className="input-control__note h_ta-start">{state.success && successMessage}</span>
          </div>
        ) : (
          <Input
            className={classNames('copy__input', props.inputClassName)}
            inputClassName={props.inputBoxClassName}
            onChange={this.updateInputValue}
            footnote={state.success ? successMessage : null}
            value={displayValue}
            ref="copy-input"
            testid={props.testid + '-input'}
            disabled={props.enableEdit ? state.success : true}
            customChildComponent={props.customInputComponent}
            i13nLabel="Copy input"
          />
        )}
        <div>
          <ButtonIcon
            iconClassName="copy__icon"
            icon="content_copy"
            onClick={this.copyToClipboard}
            className={classNames('copy__btn h_va-t h_d-ib', {
              [props.buttonClassName]: !!props.buttonClassName,
            })}
            aria-label="Copy to clipboard"
            title="Copy to clipboard"
            i13nModel={i13nModel}
            testid={props.testid}
          />
        </div>
        {props.children}
      </div>
    );
  }
}

export default Copy;
